import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaTimes, FaGamepad, FaClock, FaBullseye } from "react-icons/fa";
import { useGameSearchInfinite } from "@/hooks/overwolf/useGameSearchInfinite";
import { OWGameResponse } from "@devour/client";
import FrameOneModal from "@/components/modals/modalComponents/FrameOneModal";
import LazyImage from "../games/LazyImage";
import "./GameSearchModal.scss";

interface GameSearchModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const GameSearchModal: React.FC<GameSearchModalProps> = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [filterMode, setFilterMode] = useState<"all" | "quests">("quests"); // Default to quests

    // Use infinite scroll hook with server-side filtering
    const category = filterMode === "quests" ? "events" : "all";
    const {
        games,
        totalGames,
        isLoading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useGameSearchInfinite({
        search: searchTerm.trim() || undefined,
        category,
    });

    // Games are already filtered server-side, just need to sort
    const filteredGames = games;

    // Sort games alphabetically
    const sortedGames = useMemo(() => {
        if (!filteredGames || filteredGames.length === 0) {
            return [];
        }

        return [...filteredGames].sort((a, b) => {
            if (!a || !b) return 0;

            // Sort alphabetically
            const aName = a.name || "";
            const bName = b.name || "";
            return aName.localeCompare(bName);
        });
    }, [filteredGames]);

    // Infinite scroll trigger ref
    const loadMoreRef = useRef<HTMLDivElement>(null);

    // Calculate list dimensions
    const modalContentRef = useRef<HTMLDivElement>(null);
    const [listHeight, setListHeight] = useState(400);

    const handleGameClick = useCallback((game: OWGameResponse) => {
        if (game && game.slug) {
            navigate(`/games/${game.slug}`);
            onClose();
        }
    }, [navigate, onClose]);


    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
            onClose();
        }
    }, [onClose]);

    // Clear search and reset filter when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm("");
            setFilterMode("quests"); // Reset filter to quests (default)
        }
    }, [isOpen]);

    // Auto-focus search input when modal opens
    useEffect(() => {
        if (isOpen) {
            const searchInput = document.getElementById("game-search-input");
            if (searchInput) {
                setTimeout(() => searchInput.focus(), 100);
            }
        }
    }, [isOpen]);

    // Infinite scroll observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            { threshold: 0.1 },
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Calculate available height for the list
    useEffect(() => {
        const calculateHeight = () => {
            if (modalContentRef.current) {
                const modalHeight = modalContentRef.current.offsetHeight;
                const headerHeight = 150; // Approximate height of header + search
                const availableHeight = modalHeight - headerHeight;
                setListHeight(Math.max(300, Math.min(500, availableHeight)));
            }
        };

        if (isOpen) {
            calculateHeight();
            window.addEventListener("resize", calculateHeight);
            return () => window.removeEventListener("resize", calculateHeight);
        }
    }, [isOpen]);

    return (
        <FrameOneModal
            isOpen={isOpen}
            toggle={onClose}
            contentClassName="game-search-modal"
            usePortal={true}
            disableOverlayDismiss={false}
        >
            <div className="game-search-modal__content" ref={modalContentRef}>
                <div className="game-search-modal__header">
                    <h2 className="game-search-modal__title">
                        <FaGamepad /> All Supported Games
                    </h2>
                    <button
                        className="game-search-modal__close"
                        onClick={onClose}
                        aria-label="Close search"
                    >
                        <FaTimes />
                    </button>
                </div>

                <div className="game-search-modal__search">
                    <FaSearch className="game-search-modal__search-icon" />
                    <input
                        id="game-search-input"
                        type="text"
                        className="game-search-modal__input"
                        placeholder="Search games..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />

                    {/* Filter buttons - Quest first, then Time */}
                    <div className="game-search-modal__filters">
                        <button
                            className={`game-search-modal__filter-btn ${filterMode === "quests" ? "game-search-modal__filter-btn--active" : ""}`}
                            onClick={() => setFilterMode("quests")}
                            title="Games with quests and rewards"
                            aria-label="Show games with quests"
                        >
                            <FaBullseye />
                        </button>
                        <button
                            className={`game-search-modal__filter-btn ${filterMode === "all" ? "game-search-modal__filter-btn--active" : ""}`}
                            onClick={() => setFilterMode("all")}
                            title="All time-reward games"
                            aria-label="Show all time-reward games"
                        >
                            <FaClock />
                        </button>
                    </div>

                    {searchTerm &&
                        <button
                            className="game-search-modal__clear"
                            onClick={() => setSearchTerm("")}
                            aria-label="Clear search"
                        >
                            <FaTimes />
                        </button>
                    }
                </div>

                <div className="game-search-modal__results">
                    {error
                        ? <div className="game-search-modal__error">
                            <p>Failed to load games. Please try again later.</p>
                        </div>
                        : isLoading
                            ? <div className="game-search-modal__loading">
                                <div className="game-search-modal__spinner" />
                                <p>Loading games...</p>
                            </div>
                            : sortedGames.length === 0
                                ? <div className="game-search-modal__empty">
                                    {searchTerm
                                        ? <p>No games found matching "{searchTerm}"</p>
                                        : <p>No games available</p>
                                    }
                                </div>
                                : <>
                                    {totalGames > 0 &&
                                <div className="game-search-modal__count">
                                    {totalGames} games available
                                </div>
                                    }
                                    <div className="game-search-modal__list-container" style={{ height: listHeight, overflowY: "auto" }}>
                                        {sortedGames.map((game) => {
                                            if (!game || !game.slug) return null;

                                            return (
                                                <div key={game.gameId} className="game-search-modal__item-wrapper">
                                                    <div
                                                        className="game-search-modal__item"
                                                        onClick={() => handleGameClick(game)}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter" || e.key === " ") {
                                                                handleGameClick(game);
                                                            }
                                                        }}
                                                    >
                                                        <div className="game-search-modal__item-left">
                                                            <div className="game-search-modal__item-text">
                                                                <span className="game-search-modal__item-name">{game.name}</span>
                                                                {(game as any).steamData?.genres && (game as any).steamData.genres.length > 0 &&
                                                            <span className="game-search-modal__item-genre">
                                                                {(game as any).steamData.genres[0]}
                                                            </span>
                                                                }
                                                            </div>
                                                        </div>
                                                        <button
                                                            className="game-search-modal__item-visit"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleGameClick(game);
                                                            }}
                                                            aria-label={`View ${game.name} page`}
                                                        >
                                                            View
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* Infinite scroll trigger */}
                                        {hasNextPage &&
                                    <div ref={loadMoreRef} className="game-search-modal__load-more">
                                        {isFetchingNextPage
                                            ? <div className="game-search-modal__loading">
                                                <div className="game-search-modal__spinner" />
                                                <span>Loading more games...</span>
                                            </div>
                                            : <div style={{ height: 1 }} />
                                        }
                                    </div>
                                        }
                                    </div>
                                </>
                    }
                </div>
            </div>
        </FrameOneModal>
    );
};

export default GameSearchModal;